import type { EntitlementFailure } from '@/lib/entitlement-failure';
import type { MaterialUploadCheckpoint } from '@/lib/api/materials';
import type { ApiResponse } from '@/lib/api/types';
import type { SubjectModel, SubjectModelAnchorImage } from '@/types/subject-model';
import type { SoulSaveSession } from '@/types/subject-model';
import { runServerSoulSaveJob } from './soul-save-server-job';

export type SoulSaveStage = 'uploading' | 'upload_failed' | 'submitting' | 'submission_unknown' | 'rejected' | 'completed';
export interface SoulSavePhoto {
  id: string;
  blob?: Blob;
  filename?: string;
  remoteUrl?: string;
  receipt?: SubjectModelAnchorImage;
  checkpoint?: MaterialUploadCheckpoint;
}
export interface SoulSaveJob {
  protocol?: 'server_save_v1';
  id: string;
  ownerId: string;
  name: string;
  credits: number;
  createdAt: number;
  stage: SoulSaveStage;
  photos: SoulSavePhoto[];
  errorKey?: string;
  modelId?: string;
  entitlementFailure?: EntitlementFailure;
  recoverable?: boolean;
  sessionRegistered?: boolean;
}
export interface SoulSaveDependencies {
  assertOwner: () => void;
  persist: (job: SoulSaveJob) => Promise<void>;
  confirm: (checkpoint: MaterialUploadCheckpoint) => Promise<ApiResponse<SubjectModelAnchorImage>>;
  upload: (photo: SoulSavePhoto, checkpoint: (value: MaterialUploadCheckpoint) => Promise<void>) => Promise<SubjectModelAnchorImage>;
  create: (job: SoulSaveJob) => Promise<ApiResponse<SubjectModel>>;
  find: (id: string) => Promise<ApiResponse<SubjectModel | null>>;
  onModel: (model: SubjectModel) => void;
  server?: {
    ensure: (job: SoulSaveJob) => Promise<ApiResponse<SoulSaveSession>>;
    register: (id: string, index: number, assetId: string, checkpoint?: MaterialUploadCheckpoint) => Promise<ApiResponse<SoulSaveSession>>;
    resume?: (id: string, acceptedCredits: number) => Promise<ApiResponse<SoulSaveSession>>;
    get: (id: string) => Promise<ApiResponse<SoulSaveSession | null>>;
  };
}

function requirePhoto(photo: SubjectModelAnchorImage) {
  if (!photo?.asset_id || !photo.url) throw new Error('subjectModelUploadFailed');
  return photo;
}

/** One admission attempt. Recovery of an ambiguous admission is strictly read-only. */
export async function runSoulSaveJob(job: SoulSaveJob, deps: SoulSaveDependencies, resumeUpload = false, acceptedCredits?: number): Promise<void> {
  if (job.protocol === 'server_save_v1') return runServerSoulSaveJob(job, deps, resumeUpload, acceptedCredits);
  const save = async () => { deps.assertOwner(); await deps.persist(job); deps.assertOwner(); };
  const complete = async (model: SubjectModel) => {
    job.stage = 'completed';
    job.modelId = model.id;
    job.errorKey = undefined;
    // Original portraits leave local storage only after a durable owned receipt.
    for (const photo of job.photos) { delete photo.blob; delete photo.checkpoint; }
    await save();
    deps.onModel(model);
  };
  deps.assertOwner();
  if (job.stage === 'completed' || job.stage === 'rejected') return;
  if (job.stage === 'submitting' || job.stage === 'submission_unknown') {
    const response = await deps.find(job.id);
    deps.assertOwner();
    if (response.success !== false && !response.error && response.data?.soul_identity?.task_id) {
      await complete(response.data);
    } else {
      const changed = job.stage !== 'submission_unknown' || job.errorKey !== 'soulCreateFailed';
      job.stage = 'submission_unknown';
      job.errorKey = 'soulCreateFailed';
      if (changed) await save();
    }
    return;
  }
  if (job.stage === 'upload_failed' && !resumeUpload) return;
  let admissionIntent = false;
  try {
    job.stage = 'uploading';
    job.errorKey = undefined;
    await save();
    for (const photo of job.photos) {
      deps.assertOwner();
      if (photo.receipt) { requirePhoto(photo.receipt); continue; }
      if (photo.checkpoint) {
        const response = await deps.confirm(photo.checkpoint);
        deps.assertOwner();
        if (response.success !== false && !response.error && response.data) {
          photo.receipt = requirePhoto(response.data);
        } else if (resumeUpload && response.error?.code === 'material_upload_object_missing') {
          // Only an explicit resume plus authoritative absence permits another PUT.
          delete photo.checkpoint;
          await save();
        } else {
          throw new Error('subjectModelUploadFailed');
        }
      }
      if (!photo.receipt) {
        photo.receipt = requirePhoto(await deps.upload(photo, async (checkpoint) => {
          photo.checkpoint = checkpoint;
          await save(); // No PUT if this checkpoint cannot be committed.
        }));
      }
      delete photo.blob;
      delete photo.checkpoint;
      await save();
    }
    // This intent must be durable BEFORE POST. Even a crash before POST must
    // never cause an automatic second admission after recovery.
    admissionIntent = true;
    job.stage = 'submitting';
    await save();
    const response = await deps.create(job);
    deps.assertOwner();
    if (response.success !== false && !response.error && response.data?.soul_identity?.task_id) {
      await complete(response.data);
      return;
    }
    const status = response.error?.code === 'INSUFFICIENT_CREDITS' ? 402
      : Number(response.error?.code.match(/^HTTP_(\d+)$/)?.[1] ?? response.error?.details?.httpStatus);
    job.stage = [400, 401, 402, 403, 409, 422].includes(status) ? 'rejected' : 'submission_unknown';
    job.errorKey = status === 402 || response.error?.code === 'INSUFFICIENT_CREDITS' ? 'soulInsufficientCredits'
      : status === 409 ? 'soulPriceChanged' : 'soulCreateFailed';
    await save();
  } catch {
    // A session switch stops the old owner's workflow before further network I/O.
    deps.assertOwner();
    job.stage = admissionIntent ? 'submission_unknown' : 'upload_failed';
    job.errorKey = job.stage === 'upload_failed' ? 'subjectModelUploadFailed' : 'soulCreateFailed';
    await save();
  }
}
